import { createContext, useCallback, useMemo, type ReactNode } from "react";

import { clamp, isEqual, isNil, uniqBy } from "lodash";
import LZString from "lz-string";
import {
  BooleanParam,
  createEnumParam,
  decodeJson,
  encodeJson,
  NumberParam,
  StringParam,
  withDefault,
  type QueryParamConfig,
} from "serialize-query-params";
import { useQueryParams } from "use-query-params";

import { useUser } from "@components/user-context";
import { type Level } from "@features/overview-v2/types";
import { type TopLevelBreakdown } from "@features/sales-v3/types";
import {
  CreativesFilterClauseValueOperatorsEnum,
  CreativesSortingOrder,
  makeLandingPageRows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import {
  CustomJsonCompressedParam,
  CustomJsonParam,
  MCAttributionWindowDays,
  NBAccountingMode,
  todayLocalAsISODate,
  type DateRangeChoiceV3,
  type FixedDateInterval,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import {
  NAME_COLUMN_DEFAULT_WIDTH,
  NAME_COLUMN_ID,
  NAME_COLUMN_TITLE,
  ROWS_PER_PAGE,
  URL_COLUMN_DEFAULT_WIDTH,
  URL_COLUMN_ID,
  URL_COLUMN_TITLE,
} from "./components/table/constants";
import {
  type AdQuery,
  type ChartView,
  type ColumnConfig,
  type ColumnDef,
  type LandingPageAnalyticsContextDescriptor,
  type MetricFilter,
  type Sorting,
} from "./types";

const AccountingModeParam = createEnumParam<INBAccountingMode>(
  NBAccountingMode.keyOrder,
);

const AttributionWindowParam = createEnumParam<IMCAttributionWindowDays>(
  MCAttributionWindowDays.keyOrder,
);

const LevelParam = createEnumParam<Level>(["campaign", "adset", "ad"]);

const DateRangeParam = CustomJsonParam<FixedDateInterval | DateRangeChoiceV3>();

const BreakdownParam = CustomJsonCompressedParam<TopLevelBreakdown[]>();

const SortingParam = CustomJsonCompressedParam<Sorting>();

const MetricFilterParam = CustomJsonCompressedParam<MetricFilter[]>();

const AdQueryParam = CustomJsonCompressedParam<AdQuery | null>();

const AdvancedSearchParam = CustomJsonCompressedParam<AdQuery | null>();

const ActiveMetricsParam = CustomJsonCompressedParam<string[]>();

const ChartViewParam = createEnumParam<ChartView>(["aggregate", "split"]);

const ColumnConfigsParam = (): QueryParamConfig<ColumnConfig[]> => ({
  encode: (value) => {
    try {
      const stringified = encodeJson(value.map((e) => [e.name, e.width]))!;
      return LZString.compressToEncodedURIComponent(stringified);
    } catch (error) {
      return "";
    }
  },
  decode: (value) => {
    if (isNil(value)) {
      return [];
    }

    try {
      const decodedSuperCompressed = decodeJson(
        LZString.decompressFromEncodedURIComponent(value as string),
      );

      return decodedSuperCompressed
        .map((e: any) => {
          try {
            if (Array.isArray(e)) {
              const [name, width] = e;
              return { name, width };
            }

            return null;
          } catch (error) {
            return null;
          }
        })
        .filter(Boolean);
    } catch (error) {
      return [];
    }
  },
  equals: (a, b) => isEqual(a, b),
});

const SelectedRowsParam = (): QueryParamConfig<string[]> => ({
  encode: (value) => {
    try {
      const stringified = encodeJson(value)!;
      return LZString.compressToEncodedURIComponent(stringified);
    } catch (error) {
      return "";
    }
  },
  decode: (value) => {
    if (isNil(value)) {
      return [];
    }

    try {
      return decodeJson(
        LZString.decompressFromEncodedURIComponent(value as string),
      );
    } catch (error) {
      return [];
    }
  },
  equals: (a, b) => isEqual(a, b),
});

const getDefaultCellWidth = (name: string) => {
  return clamp(140, name.length * 9, 200);
};

const createColumnDefinition = (metrics: SalesPageMetric[]) => {
  return [
    {
      name: URL_COLUMN_ID,
      title: URL_COLUMN_TITLE,
      width: URL_COLUMN_DEFAULT_WIDTH,
    },
    {
      name: NAME_COLUMN_ID,
      title: NAME_COLUMN_TITLE,
      width: NAME_COLUMN_DEFAULT_WIDTH,
    },
    ...metrics.map((metric) => {
      const columnDef: ColumnDef = {
        name: metric.id,
        title: metric.name,
        width: getDefaultCellWidth(metric.name),
        metric,
      };

      return columnDef;
    }),
  ];
};

export const DEFAULT_SORTING: Sorting = { id: "rev", order: "desc" };
export const DEFAULT_ACTIVE_METRICS = ["rev"];

export const LandingPageAnalyticsContext =
  createContext<LandingPageAnalyticsContextDescriptor | null>(null);

export const LandingPageAnalyticsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useUser();

  const [filters, setFilters] = useQueryParams(
    {
      attributionModel: withDefault(
        StringParam,
        user.getDefaultAttributionModel(""),
      ),
      attributionWindow: withDefault(AttributionWindowParam, "1" as const),
      accountingMode: withDefault(AccountingModeParam, "accrual" as const),
      level: withDefault(LevelParam, "campaign" as const),
      dateRange: withDefault(DateRangeParam, "last7Days"),
      breakdownFilters: withDefault(BreakdownParam, []),
      adQuery: withDefault(AdQueryParam, null),
      urlQuery: withDefault(StringParam, null),
      advancedSearch: withDefault(AdvancedSearchParam, null),
    },
    { updateType: "pushIn" },
  );

  const [tableState, setTableState] = useQueryParams(
    {
      columnConfigs: withDefault(ColumnConfigsParam(), [] as const),
      selectedRows: withDefault(SelectedRowsParam(), [] as const),
      sorting: withDefault(SortingParam, { ...DEFAULT_SORTING }),
      metricFilters: withDefault(MetricFilterParam, [] as const),
      page: withDefault(NumberParam, 1),
    },
    { updateType: "pushIn" },
  );

  const [chartState, setChartState] = useQueryParams(
    {
      chartView: withDefault(ChartViewParam, "aggregate" as const),
      alignAxesScale: withDefault(BooleanParam, false),
      activeMetrics: withDefault(ActiveMetricsParam, DEFAULT_ACTIVE_METRICS),
    },
    { updateType: "pushIn" },
  );

  const setAttributionModel = useCallback(
    (value: string) => {
      setFilters({ attributionModel: value });
    },
    [setFilters],
  );

  const setAttributionWindow = useCallback(
    (value: IMCAttributionWindowDays) => {
      setFilters({ attributionWindow: value });
    },
    [setFilters],
  );

  const setAccountingMode = useCallback(
    (value: INBAccountingMode) => {
      setFilters({ accountingMode: value });
    },
    [setFilters],
  );

  const setLevel = useCallback(
    (value: Level) => {
      setFilters({ level: value });
    },
    [setFilters],
  );

  const setDateRange = useCallback(
    (value: FixedDateInterval | DateRangeChoiceV3) => {
      setFilters({ dateRange: value });
    },
    [setFilters],
  );

  const setBreakdownFilters = useCallback(
    (value: TopLevelBreakdown[]) => {
      setFilters({ breakdownFilters: value });
    },
    [setFilters],
  );

  const setAdQuery = useCallback(
    (value: AdQuery | null) => {
      setFilters({ adQuery: value });
    },
    [setFilters],
  );

  const setUrlQuery = useCallback(
    (value: string | null) => {
      setFilters({ urlQuery: value });
    },
    [setFilters],
  );

  const setAdvancedSearch = useCallback(
    (value: any | null) => {
      setFilters({ advancedSearch: value });
    },
    [setFilters],
  );

  const setSorting = useCallback(
    (value: Sorting) => {
      setTableState({ sorting: value });
    },
    [setTableState],
  );

  const setMetricFilters = useCallback(
    (value: MetricFilter[]) => {
      setTableState({ metricFilters: value });
    },
    [setTableState],
  );

  const setColumnConfigs = useCallback(
    (value: ColumnConfig[]) => {
      setTableState({ columnConfigs: value });
    },
    [setTableState],
  );

  const setSelectedRows = useCallback(
    (value: string[]) => {
      setTableState({ selectedRows: value });
    },
    [setTableState],
  );

  const setPage = useCallback(
    (value: number) => {
      setTableState({ page: value });
    },
    [setTableState],
  );

  const setChartView = useCallback(
    (value: ChartView) => {
      setChartState({ chartView: value });
    },
    [setChartState],
  );

  const setAlignAxesScale = useCallback(
    (value: boolean) => {
      setChartState({ alignAxesScale: value });
    },
    [setChartState],
  );

  const setActiveMetrics = useCallback(
    (value: string[]) => {
      setChartState({ activeMetrics: value });
    },
    [setChartState],
  );

  const dateRangeInterval = useMemo(
    () => parseDateRangeArgument(filters.dateRange, todayLocalAsISODate()),
    [filters.dateRange],
  );

  const metrics = useMemo(() => {
    const result = makeLandingPageRows(
      filters.attributionModel,
      filters.attributionWindow,
      filters.accountingMode,
      user.attributionMethodEnum,
    ).map((metric) => {
      return {
        ...metric,
        id: metric.metadata?.overviewPageMetricId,
      };
    });

    return uniqBy(result, "id");
  }, [filters, user]);

  const columns = useMemo(() => {
    const columnDefinitions = createColumnDefinition(metrics);

    return columnDefinitions.map((col) => {
      const config = tableState.columnConfigs.find((e) => e.name === col.name);

      if (config) {
        return { ...col, width: config.width };
      } else {
        return col;
      }
    });
  }, [tableState.columnConfigs, metrics]);

  const reportTableVariables = useMemo(() => {
    const flatBreakdownFilters: { breakdown: string; value: string }[] = [];

    for (const breakdown of filters.breakdownFilters) {
      for (const value of breakdown.values) {
        flatBreakdownFilters.push({ breakdown: breakdown.key, value });
      }
    }

    return {
      attributionMethod: filters.attributionModel,
      accountingMode: filters.accountingMode,
      attributionWindow: filters.attributionWindow,
      level: filters.level,
      dateRange: dateRangeInterval,
      breakdownFilters: flatBreakdownFilters,
      adQuery: filters.adQuery,
      urlQuery: filters.urlQuery,
      advancedSearch: filters.advancedSearch,
      metricFilters: tableState.metricFilters.map(
        ({ id, operator, value }) => ({
          id,
          operator: CreativesFilterClauseValueOperatorsEnum[operator],
          value,
        }),
      ),
      sorting: {
        id: tableState.sorting.id,
        order: CreativesSortingOrder[tableState.sorting.order],
      },
      page: tableState.page,
      perPage: ROWS_PER_PAGE,
    };
  }, [
    filters.breakdownFilters,
    filters.attributionModel,
    filters.accountingMode,
    filters.attributionWindow,
    filters.level,
    dateRangeInterval,
    filters.adQuery,
    filters.urlQuery,
    filters.advancedSearch,
    tableState.metricFilters,
    tableState.sorting,
    tableState.page,
  ]);

  return (
    <LandingPageAnalyticsContext.Provider
      value={{
        metrics,
        columns,
        filters: { ...filters, dateRangeInterval },
        tableState,
        chartState,
        reportTableVariables,
        setAttributionModel,
        setAttributionWindow,
        setAccountingMode,
        setLevel,
        setDateRange,
        setBreakdownFilters,
        setAdQuery,
        setUrlQuery,
        setAdvancedSearch,
        setSorting,
        setMetricFilters,
        setColumnConfigs,
        setSelectedRows,
        setPage,
        setChartView,
        setAlignAxesScale,
        setActiveMetrics,
      }}
    >
      {children}
    </LandingPageAnalyticsContext.Provider>
  );
};
